/**
 * @file date-time.component.tsx
 * @description Компонент работы с датой и временем. Используется библиотека @dhx/suite для календаря с выбором времени
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-16
 * @dependencies ['react-imask', '@dhx/suite', '@components/ui/base/text-input', '@consta/icons/IconCalendar', 'moment', 'classnames', '@consta/uikit/useGlobalKeys']
 * @related-files ['src/components/ui/base/text-input/text-input.component.tsx']
 */

import { useIMask, IMask, ReactMaskOpts } from 'react-imask';
import { useEffect, useRef, useMemo } from 'react';
import * as dhx from '@src/modules/dhx-suite/codebase/suite.js';
const { Calendar, Popup, i18n } = dhx;
import '@src/modules/dhx-suite/codebase/suite.min.css';
import { TextInput } from '@components/ui/base/text-input';
import { IconCalendar } from '@consta/icons/IconCalendar';
import moment from 'moment';
import cn from 'classnames';
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys';
import { stringToTimestamp } from '@src/utils/date-time-helper';

/**
 * Props для компонента выбора даты и времени
 */
interface IDateTimePickerProps {
    /** Текст метки для поля ввода даты и времени */
    label?: string;
    /** Значение даты и времени в unix timestamp формате */
    value: number;
    /** Обработчик изменения даты и времени */
    onChange?: ((value: number | null) => void) | null;
    /** Режим только для чтения */
    readOnly?: boolean;
    /** Функция добавления модального окна в очередь */
    addModal: () => void;
    /** Уникальный идентификатор модального окна */
    modalId: string;
    /** Текущая очередь модальных окон */
    currentModalsQueue: string[];
    /** Функция удаления модального окна из очереди по ID */
    removeModalFromQueueById: () => void;
    /** CSS класс для дополнительной стилизации */
    className?: string;
}

export const DateTimePicker = ({
    label,
    value = moment().unix(),
    onChange,
    readOnly = Boolean(!onChange),
    addModal,
    modalId,
    currentModalsQueue,
    removeModalFromQueueById,
    className,
    ...props
}: IDateTimePickerProps) => {
    i18n.setLocale('calendar', {
        monthsShort: moment.monthsShort(),
        months: moment.months(),
        daysShort: moment.weekdaysShort(),
        days: moment.weekdays(),
        cancel: 'Отмена'
    });
    i18n.setLocale('timepicker', {
        hours: 'Часы',
        minutes: 'Минуты',
        save: 'Сохранить'
    });
    const ref = useRef<HTMLInputElement>(null);
    const itemsRef = useRef<string[] | null>(null);
    const momentFormat = useMemo(() => 'DD.MM.YYYY HH:mm', []);
    const datePickerValue = useMemo(() => (value && value > 0 ? moment(value).format(momentFormat) : ''), [value]);

    const {
        ref: inputRef,
        value: iMaskValue,
        setValue
    } = useIMask<HTMLInputElement, ReactMaskOpts>({
        mask: Date,
        pattern: momentFormat,
        min: new Date(1970, 0, 1),
        max: new Date(2030, 0, 1),
        format: (date: Date | null) => (date ? moment(date).format(momentFormat) : ''),
        parse: (str: string) => moment(str, momentFormat).toDate(),
        blocks: {
            YYYY: {
                mask: IMask.MaskedRange,
                from: 1970,
                to: 2030
            },
            MM: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 12
            },
            DD: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 31
            },
            HH: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 23
            },
            mm: {
                mask: IMask.MaskedRange,
                from: 0,
                to: 59,
                maxLength: 2,
                autofix: true
            }
        }
    });
    const popup = useMemo(() => new Popup(), []);
    const calendar = new Calendar('', {
        dateFormat: '%d.%m.%Y %H:%i',
        weekStart: 'monday',
        timePicker: true,
        value: datePickerValue || undefined,
        date: datePickerValue || undefined,
        mark: (date) => {
            if (moment(date).format('L') === moment().format('L')) {
                return 'dhx_calendar-day--current-date';
            }

            return '';
        }
    });

    useEffect(() => {
        itemsRef.current = currentModalsQueue;
    }, [currentModalsQueue]);

    const changeValueHandler = (newValue: string | null) => {
        onChange && onChange(stringToTimestamp(newValue, momentFormat));
    };

    const showCalendarHandler = () => {
        if (!inputRef.current) {
            return;
        }

        inputRef.current.select();
        addModal();
        ref.current && popup.show(ref.current);
    };

    const changeInputHandler = (newValue: string | null | undefined) => {
        if (!newValue) {
            calendar.clear();
            changeValueHandler(null);
            return;
        }

        if (moment(newValue, momentFormat, true).isValid()) {
            calendar.setValue(newValue);
            changeValueHandler(newValue);
        }

        setValue(newValue);
    };

    popup.attach(calendar);

    useEffect(() => {
        if (datePickerValue) {
            setValue(datePickerValue);
            calendar.setValue(datePickerValue);
        }

        popup.events.on('afterHide', function () {
            removeModalFromQueueById();
            return true;
        });

        calendar.events.on('change', (_date: Date, _oldDate: Date, isClick: boolean) => {
            const newVal = calendar.getValue();
            const stringValue = newVal instanceof Date ? moment(newVal).format(momentFormat) : newVal;
            setValue(stringValue);

            if (isClick) {
                changeValueHandler(stringValue);
            }

            popup.hide();
        });

        return () => {
            calendar && calendar.destructor();
        };
    }, []);

    useEffect(() => {
        datePickerValue ? calendar.setValue(datePickerValue) : calendar.clear();
        calendar.paint();
    }, [datePickerValue]);

    useGlobalKeys({
        Escape: () => {
            if (itemsRef.current?.at(-1) === modalId) {
                popup.hide();
            }
        }
    });

    return (
        <TextInput
            size="s"
            iconSize="xs"
            label={label}
            value={iMaskValue}
            ref={ref}   
            inputRef={inputRef}
            readOnly={readOnly}
            rightSide={IconCalendar}
            onFocus={() => (onChange ? showCalendarHandler() : null)}
            onChange={changeInputHandler}
            className={cn('DatePickerXMes', className, { 'cursor-pointer': !readOnly })}
            {...props}
        />
    );
};
